import { useState } from "react";
import { FileText, Download, Image as ImageIcon, X, AlertTriangle } from "lucide-react";
import useBlobFile, { downloadFileById } from "@/hooks/useBlobFile";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImageThumb({ attachment, onOpen }) {
  const { url, loading, error } = useBlobFile(attachment.id, true);
  const isHEIC = (attachment.mime_type || "").includes("heic") || (attachment.mime_type || "").includes("heif");

  if (isHEIC) {
    // HEIC — browser can't preview; render a clean card with download instead
    return (
      <button
        type="button"
        onClick={() => downloadFileById(attachment.id, attachment.original_filename)}
        data-testid={`attachment-heic-card-${attachment.id}`}
        className="border border-ochre/30 bg-ochre/6 rounded-md flex items-center gap-2 pl-2 pr-2.5 py-1.5 text-xs hover:border-ochre/60"
      >
        <div className="w-8 h-8 rounded flex-shrink-0 flex items-center justify-center bg-ochre/15 border border-ochre/30">
          <ImageIcon className="w-4 h-4 text-ochre-ink" />
        </div>
        <div className="min-w-0 text-left">
          <div className="truncate font-medium text-ink" title={attachment.original_filename}>
            {attachment.original_filename || "Slika"}
          </div>
          <div className="text-ink-muted text-[11px]">HEIC · {fmtSize(attachment.size)} · preuzmi</div>
        </div>
        <Download className="w-3.5 h-3.5 text-ochre-ink ml-1" />
      </button>
    );
  }

  if (error) {
    return (
      <div
        className="w-24 h-24 rounded border border-terracotta/30 bg-terracotta/6 flex items-center justify-center text-[10px] text-terracotta text-center px-1"
        data-testid={`attachment-thumb-error-${attachment.id}`}
      >
        <AlertTriangle className="w-4 h-4 mr-0.5" /> Neuspešno
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => url && onOpen({ ...attachment, url })}
      className="w-24 h-24 rounded border border-line overflow-hidden bg-surface-secondary flex items-center justify-center hover:border-forest/40"
      data-testid={`attachment-thumb-${attachment.id}`}
    >
      {loading ? (
        <span className="w-4 h-4 border-2 border-ink-muted/30 border-t-forest rounded-full animate-spin" />
      ) : url ? (
        <img src={url} alt={attachment.original_filename || ""} className="w-full h-full object-cover" />
      ) : (
        <ImageIcon className="w-6 h-6 text-ink-muted" />
      )}
    </button>
  );
}

function PdfChip({ attachment }) {
  return (
    <button
      type="button"
      onClick={() => downloadFileById(attachment.id, attachment.original_filename)}
      data-testid={`attachment-pdf-${attachment.id}`}
      className="inline-flex items-center gap-2 border border-terracotta/30 bg-terracotta/6 rounded-md pl-2 pr-2.5 py-1.5 text-xs hover:border-terracotta/60"
    >
      <div className="w-8 h-8 rounded flex-shrink-0 flex items-center justify-center bg-terracotta/12 border border-terracotta/30">
        <FileText className="w-4 h-4 text-terracotta" />
      </div>
      <div className="min-w-0 text-left">
        <div className="truncate font-medium text-ink" title={attachment.original_filename}>
          {attachment.original_filename || "PDF dokument"}
        </div>
        <div className="text-ink-muted text-[11px]">PDF · {fmtSize(attachment.size)} · otvori</div>
      </div>
      <Download className="w-3.5 h-3.5 text-terracotta ml-1" />
    </button>
  );
}

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 flex flex-col items-center justify-center p-4"
      data-testid="attachment-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
        data-testid="attachment-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={item.url}
        alt={item.original_filename || ""}
        className="max-h-[80vh] max-w-full object-contain rounded"
      />
      <div className="mt-3 flex items-center gap-3 text-white/80 text-xs">
        <span>{item.original_filename}</span>
        <button
          onClick={(e) => { e.stopPropagation(); downloadFileById(item.id, item.original_filename); }}
          className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded px-2 py-1"
          data-testid="attachment-lightbox-download"
        >
          <Download className="w-3.5 h-3.5" /> Preuzmi
        </button>
      </div>
    </div>
  );
}

export default function AttachmentPreview({ attachments = [] }) {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments.length) return null;
  const images = attachments.filter((a) => (a.mime_type || "").startsWith("image/"));
  const docs = attachments.filter((a) => !(a.mime_type || "").startsWith("image/"));
  const heic = images.filter((a) => (a.mime_type || "").includes("heic") || (a.mime_type || "").includes("heif"));
  const previewable = images.filter((a) => !heic.includes(a));

  return (
    <div className="mt-2 space-y-1.5" data-testid="attachment-list">
      {previewable.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {previewable.map((a) => (
            <ImageThumb key={a.id} attachment={a} onOpen={setLightbox} />
          ))}
        </div>
      )}
      {(heic.length + docs.length) > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {heic.map((a) => (
            <ImageThumb key={a.id} attachment={a} onOpen={setLightbox} />
          ))}
          {docs.map((a) => (
            <PdfChip key={a.id} attachment={a} />
          ))}
        </div>
      )}
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
